import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient, ApiClientError } from '@/api/client'
import { localDatabase } from '@/db/localDatabase'
import type { ApiRoute, ApiWaypoint, WaypointData } from '@/types/api'
import { routeLogger } from '@/utils/logger'

export const useRoutesStore = defineStore('routes', () => {
  // State
  const routes = ref<ApiRoute[]>([])
  const selectedRoute = ref<ApiRoute | null>(null)
  const isLoading = ref(false)
  const isSyncing = ref(false)
  const isInitialized = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const activeRoutes = computed(() =>
    routes.value.filter((r) => !r.is_finished && !r.is_deleted)
  )

  const finishedRoutes = computed(() =>
    routes.value.filter((r) => r.is_finished && !r.is_deleted)
  )

  const sortedRoutes = computed(() => {
    return [...routes.value]
      .filter((r) => !r.is_deleted)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  })

  // Actions
  async function initialize(): Promise<void> {
    if (isInitialized.value) return

    isLoading.value = true
    error.value = null

    try {
      // Load routes from local database
      routes.value = await localDatabase.getAllRoutes()
      isInitialized.value = true
    } catch (e) {
      error.value = 'Failed to load local data'
      routeLogger.error('Failed to initialize routes from local database:', e)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchRoutes(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.listRoutes()
      routes.value = response.routes
      // Save fetched routes to local database
      await localDatabase.saveRoutes(response.routes)
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'Failed to fetch routes'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function syncRoutes(): Promise<void> {
    isSyncing.value = true
    error.value = null

    try {
      const response = await apiClient.syncRoutes({
        routes: routes.value
      })
      routes.value = response.routes
      // Save synced routes to local database
      await localDatabase.saveRoutes(response.routes)
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'Failed to sync routes'
      }
      throw e
    } finally {
      isSyncing.value = false
    }
  }

  async function createRoute(title: string, userId: string): Promise<ApiRoute> {
    const now = new Date().toISOString()
    const newRoute: ApiRoute = {
      id: crypto.randomUUID(),
      user_id: userId,
      slug: generateSlug(title),
      title,
      start_place: undefined,
      end_place: undefined,
      vessel_name: undefined,
      vessel_type: undefined,
      created_at: now,
      last_update: now,
      distance: 0,
      is_finished: false,
      is_deleted: false,
      waypoints: []
    }
    routes.value.push(newRoute)
    // Persist to local database
    await localDatabase.saveRoute(newRoute)
    return newRoute
  }

  async function updateRoute(routeId: string, updates: Partial<ApiRoute>): Promise<void> {
    const index = routes.value.findIndex((r) => r.id === routeId)
    if (index !== -1) {
      routes.value[index] = {
        ...routes.value[index],
        ...updates,
        last_update: new Date().toISOString()
      }
      if (selectedRoute.value?.id === routeId) {
        selectedRoute.value = routes.value[index]
      }
      // Persist to local database
      await localDatabase.saveRoute(routes.value[index])
    }
  }

  async function deleteRoute(routeId: string): Promise<void> {
    const index = routes.value.findIndex((r) => r.id === routeId)
    if (index !== -1) {
      routes.value[index] = {
        ...routes.value[index],
        is_deleted: true,
        last_update: new Date().toISOString()
      }
      // Persist to local database (soft delete - keep for sync)
      await localDatabase.saveRoute(routes.value[index])
    }
  }

  function selectRoute(routeId: string | null): void {
    if (routeId === null) {
      selectedRoute.value = null
    } else {
      selectedRoute.value = routes.value.find((r) => r.id === routeId) || null
    }
  }

  async function addWaypoint(routeId: string, waypoint: Omit<ApiWaypoint, 'id' | 'route_id'>): Promise<ApiWaypoint> {
    const route = routes.value.find((r) => r.id === routeId)
    if (!route) {
      throw new Error('Route not found')
    }

    const newWaypoint: ApiWaypoint = {
      id: crypto.randomUUID(),
      route_id: routeId,
      ...waypoint
    }

    route.waypoints.push(newWaypoint)
    route.last_update = new Date().toISOString()

    // Recalculate distance
    if (route.waypoints.length > 1) {
      route.distance = calculateTotalDistance(route.waypoints)
    }

    // Persist to local database
    await localDatabase.saveRoute(route)

    return newWaypoint
  }

  async function updateWaypoint(
    routeId: string,
    waypointId: string,
    updates: Partial<ApiWaypoint>
  ): Promise<void> {
    const route = routes.value.find((r) => r.id === routeId)
    if (!route) return

    const waypointIndex = route.waypoints.findIndex((w) => w.id === waypointId)
    if (waypointIndex !== -1) {
      route.waypoints[waypointIndex] = {
        ...route.waypoints[waypointIndex],
        ...updates,
        last_update: new Date().toISOString()
      }
      route.last_update = new Date().toISOString()
      // Persist to local database
      await localDatabase.saveRoute(route)
    }
  }

  async function deleteWaypoint(routeId: string, waypointId: string): Promise<void> {
    const route = routes.value.find((r) => r.id === routeId)
    if (!route) return

    route.waypoints = route.waypoints.filter((w) => w.id !== waypointId)
    route.last_update = new Date().toISOString()

    // Recalculate distance
    if (route.waypoints.length > 1) {
      route.distance = calculateTotalDistance(route.waypoints)
    } else {
      route.distance = 0
    }

    // Persist to local database
    await localDatabase.saveRoute(route)
  }

  // Helper functions
  function generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  function calculateTotalDistance(waypoints: ApiWaypoint[]): number {
    let total = 0
    for (let i = 1; i < waypoints.length; i++) {
      total += calculateDistance(
        waypoints[i - 1].latitude,
        waypoints[i - 1].longitude,
        waypoints[i].latitude,
        waypoints[i].longitude
      )
    }
    return Math.round(total * 10) / 10 // Round to 1 decimal
  }

  // Haversine formula for distance between two coordinates (in nautical miles)
  function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3440.065 // Earth's radius in nautical miles
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  function toRad(deg: number): number {
    return (deg * Math.PI) / 180
  }

  // Parse waypoint data JSON
  function parseWaypointData(waypoint: ApiWaypoint): WaypointData {
    if (!waypoint.data) return {}
    try {
      return JSON.parse(waypoint.data) as WaypointData
    } catch {
      return {}
    }
  }

  // Stringify waypoint data
  function stringifyWaypointData(data: WaypointData): string {
    return JSON.stringify(data)
  }

  return {
    // State
    routes,
    selectedRoute,
    isLoading,
    isSyncing,
    isInitialized,
    error,
    // Getters
    activeRoutes,
    finishedRoutes,
    sortedRoutes,
    // Actions
    initialize,
    fetchRoutes,
    syncRoutes,
    createRoute,
    updateRoute,
    deleteRoute,
    selectRoute,
    addWaypoint,
    updateWaypoint,
    deleteWaypoint,
    // Helpers
    parseWaypointData,
    stringifyWaypointData
  }
})
